import { Dropdown, Table } from "antd";
import { get, isEmpty, isNil } from "lodash";
import { useTranslation } from "react-i18next";
import { MdMoreHoriz, MdOutlineDescription } from "react-icons/md";
import EmptyState from "shared/components/EmptyState";
import Input from "shared/components/Input";
import Modal from "shared/components/Modal";
import Spin from "shared/components/Spin";
import { NAME_SPACES } from "shared/locales/constants";

export const ROW_ACTIONS = { DOWNLOAD: "download", DELETE: "delete" };

export const usageNames = (document) =>
  (get(document, "usages") || [])
    .map((usage) =>
      (get(usage, "insurancePlans") || []).map(({ name }) => name).join(" + "),
    )
    .join(" ; ");

const stopPropagation = (event) => event.stopPropagation();

const View = ({
  loaded,
  loading,
  documents,
  visible,
  search,
  onSearch,
  onOpen,
  onAction,
  deleting,
  onConfirmDelete,
  onCancelDelete,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS);
  const STATIC = t("STATIC", { returnObjects: true });

  const menuItems = [
    { key: ROW_ACTIONS.DOWNLOAD, label: STATIC.ACTIONS.DOWNLOAD },
    { type: "divider" },
    { key: ROW_ACTIONS.DELETE, label: STATIC.ACTIONS.DELETE, danger: true },
  ];

  const columns = [
    {
      title: STATIC.COLUMNS.DOCUMENT,
      dataIndex: "name",
      render: (name) => <span className="dynamic-documents__name">{name}</span>,
    },
    {
      title: STATIC.COLUMNS.TYPE,
      dataIndex: "type",
      width: 200,
      render: (type) => STATIC.TYPES[type] ?? type,
    },
    {
      title: STATIC.COLUMNS.USED_IN,
      key: "usages",
      render: (_, document) => usageNames(document) || STATIC.USES.NONE,
    },
    {
      title: "",
      key: "actions",
      align: "right",
      width: 56,
      render: (_, document) => (
        <span onClick={stopPropagation}>
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{
              items: menuItems,
              onClick: ({ key }) => onAction(key, document),
            }}
          >
            <button
              type="button"
              className="dynamic-documents__more"
              aria-label={STATIC.COLUMNS.ACTIONS}
              title={STATIC.COLUMNS.ACTIONS}
            >
              <MdMoreHoriz size={20} />
            </button>
          </Dropdown>
        </span>
      ),
    },
  ];

  const content = () => {
    if (!loaded) return loading ? <Spin /> : null;
    if (isEmpty(documents))
      return (
        <div className="dynamic-documents__empty">
          <EmptyState title={STATIC.EMPTY} icon={MdOutlineDescription} />
        </div>
      );
    return (
      <>
        <div className="dynamic-documents__toolbar">
          <div className="dynamic-documents__search">
            <Input
              type="search"
              placeholder={STATIC.SEARCH}
              value={search}
              allowClear
              onChange={({ target: { value } }) => onSearch(value)}
            />
          </div>
        </div>
        <Table
          dataSource={visible}
          className="table__wrapper"
          columns={columns}
          rowKey="id"
          pagination={false}
          scroll={{ x: true }}
          locale={{ emptyText: STATIC.NO_MATCH }}
          rowClassName="dynamic-documents__list-row"
          onRow={(document) => ({ onClick: () => onOpen(document) })}
        />
      </>
    );
  };

  return (
    <>
      {content()}
      <Modal
        visible={!isNil(deleting)}
        message={STATIC.DELETE.MESSAGE}
        child={
          <p className="dynamic-documents__dialog-text">
            {t("STATIC.DELETE.DESCRIPTION", { name: get(deleting, "name") })}
          </p>
        }
        okText={STATIC.DELETE.OK}
        cancelText={STATIC.DELETE.CANCEL}
        onOk={onConfirmDelete}
        onCancel={onCancelDelete}
      />
    </>
  );
};

export default View;
